import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';
import axios from 'axios';
import LessonCard from './LessonCard'
import IconCoins from '../../components/icons/IconCoins';
import IconClock from '../../components/icons/IconClock';
import language from '../../variables/language';

const LUXON_LOCALE_BY_LANG = { en: 'en', ua: 'uk', pl: 'pl' };
// backend's ScheduleDay.dayOfWeek is a raw Java enum name ("MONDAY") - never
// localized, so it's ignored entirely and recomputed from the (real) date
// instead, same convention every other modernized list page in this app
// uses (Events.jsx/GroupLessonsList.jsx weekdayInfo helpers).
const CARD_ANIM_MS = 240;

export default function LessonsColumn({ day, today, refresh, onOpenLesson, url, enterDelay }) {
   const navigate = useNavigate();
   const [items, setItems] = useState([]);
   const [loading, setLoading] = useState(true);
   const prevIdsRef = useRef(new Set());
   const lang = language[language.lang];
   const locale = LUXON_LOCALE_BY_LANG[language.lang] || 'en';

   useEffect(() => {
      setLoading(true);
      axios.get(url + '?date=' + day.date, { withCredentials: true })
         .then(response => {
            const fresh = (Array.isArray(response.data) ? response.data : [])
               .slice()
               .sort((a, b) => (a.start || '').localeCompare(b.start || ''));
            const freshIds = new Set(fresh.map(l => l.id));
            const prevIds = prevIdsRef.current;

            setItems(prevItems => {
               // cards present before this fetch but gone now (deleted, or
               // moved to a different date) get one more render tagged
               // "leaving" so their exit animation can play, instead of
               // just vanishing the instant the new data lands.
               const leavingCards = prevItems
                  .filter(it => prevIds.has(it.id) && !freshIds.has(it.id) && !it._leaving)
                  .map(it => ({ ...it, _leaving: true }));
               if (leavingCards.length > 0) {
                  window.setTimeout(() => {
                     setItems(cur => cur.filter(it => !leavingCards.some(l => l.id === it.id)));
                  }, CARD_ANIM_MS);
               }
               return [...fresh, ...leavingCards];
            });
            prevIdsRef.current = freshIds;
            setLoading(false);
         })
         .catch(err => {
            if (err.response && err.response.status === 401) navigate('/login');
            setLoading(false);
         });
      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [day.date, refresh, url])

   const dt = DateTime.fromFormat(day.date, 'yyyy-MM-dd').setLocale(locale);
   const isToday = day.date === today;
   const visibleItems = items.filter(it => !it._leaving);

   return (
      <div className={'schedule-day-column' + (isToday ? ' is-today' : '')} style={{ animationDelay: (enterDelay || 0) + 'ms' }}>
         {isToday ? <span className='schedule-day-today-pill'>{lang.today}</span> : ''}
         <div className='schedule-day-header'>
            <div className='schedule-day-weekday-row'>
               <span className='schedule-day-weekday'>{dt.isValid ? dt.toFormat('cccc') : day.date}</span>
               <span className='schedule-day-date'>{dt.isValid ? dt.toFormat('dd.MM') : ''}</span>
            </div>
            <div className='schedule-day-stats'>
               <span className='schedule-day-stat'><IconClock />{day.hours || '0:0'}</span>
               <span className='schedule-day-stat'><IconCoins />{day.earned || 0} zł</span>
            </div>
         </div>
         <div className='schedule-day-body'>
            {loading && items.length === 0 ? (
               <div className='schedule-day-loading'>
                  <div className='schedule-skeleton-card' />
                  <div className='schedule-skeleton-card' />
               </div>
            ) : items.length === 0 ? (
               <div className='schedule-day-empty'>{lang.groupCard.noLessons}</div>
            ) : (
               items.map((item, index) => (
                  <LessonCard key={item.id} item={item} today={today}
                     index={item._leaving ? 0 : visibleItems.indexOf(item)}
                     leaving={!!item._leaving}
                     onOpen={onOpenLesson} />
               ))
            )}
         </div>
      </div>
   )
}
